import React from 'react';
import { Box, FormLabel, TextField } from '@mui/material';
// import ToolTipComponent from '../ToolTip';
import { Controller } from 'react-hook-form';
import CustomPopover from './CustomPopover';
interface Props {
  id: string;
  label?: string;
  placeholder?: string;
  name: string;
  type?: string;
  helperText?: any;
  error?: boolean;
  disabled?: boolean;
  // value?: string | number;
  defaultValue?: string | number;
  variant?: 'outlined' | 'standard' | 'filled' | undefined;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  onBlur?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  value?: string;
  startAdornment?: React.ReactElement | null;
  endAdornment?: React.ReactElement | null;
  control?: null | any;
  tooltip?: boolean;
  popoverContent?: React.ReactElement | null;
  requiredField?: boolean;
  maxLength?: any;
  inputProps?: any;
  onKeyPress?: any;
  onKeyUp?: any;
  phone?: boolean;
  getOnChangeValues?: any;
  readOnly?: boolean;
  onBlurMethod?: any;
  zipCode?: boolean;
  maxValue?: number;
  highLighted?: boolean;
  // When true, restricts input to alphabetic characters and spaces
  onlyAlphabets?: boolean;
}

/**
 * Customizable input component with adornments, validation, and support for controlled input.
 * @component
 * @example
 *   <Input
 *     id="username"
 *     label="Username"
 *     placeholder="Enter username"
 *     name="username"
 *     type="text"
 *     helperText="Required field"
 *     error={false}
 *     disabled={false}
 *     variant="outlined"
 *     value="user123"
 *     onChange={handleChange}
 *     startAdornment={<Icon />}
 *     endAdornment={<Icon />}
 *     requiredField={true}
 *     maxLength={30}
 *   />
 * @prop {string} id - Unique identifier for the input element.
 * @prop {string} label - Text label displayed above the input field.
 * @prop {string} placeholder - Placeholder text shown inside the input field.
 * @prop {string} name - Name attribute for the input element, useful for form submission.
 * @prop {string} type - Specifies the input type, such as 'text', 'password', etc.
 * @prop {string} helperText - Helper or cautionary text displayed below the input field.
 * @prop {boolean} error - Boolean indicating whether the input is in an error state.
 * @prop {boolean} disabled - Determines if the input is disabled from interaction.
 * @prop {string} variant - Specifies the input variant, such as 'outlined' or 'filled'.
 * @prop {string} value - Current value of the input field.
 * @prop {function} onChange - Function to call when the input value changes.
 * @prop {ReactNode} startAdornment - Component or element to be displayed at the start of the input.
 * @prop {ReactNode} endAdornment - Component or element to be displayed at the end of the input.
 * @prop {boolean} requiredField - Indicates if the field is required.
 * @prop {number} maxLength - Maximum allowed length of the input value.
 * @prop {boolean} readOnly - Determines if the input is read-only.
 * @description
 *   - This component prevents default scrolling behavior with the mouse wheel when on input.
 *   - Implements validation logic to restrict input based on specific conditions (e.g., no leading spaces).
 *   - Integrates form control logic using Controller from react-hook-form if available.
 *   - Supports custom popover tooltips for additional guidance on input fields.
 */
const InputField: React.FC<Props> = props => {
  const {
    id,
    label,
    placeholder,
    name,
    type,
    helperText,
    error,
    disabled,
    defaultValue,
    variant,
    value,
    onBlur,
    startAdornment,
    endAdornment,
    onChange,
    control,
    tooltip,
    popoverContent,
    onKeyDown,
    onPaste,
    requiredField,
    maxLength,
    inputProps,
    onKeyPress,
    onKeyUp,
    phone,
    getOnChangeValues,
    readOnly,
    onBlurMethod = false,
    zipCode,
    maxValue,
    highLighted,
    onlyAlphabets,
  } = props;

  const handleWheel = (event: any) => {
    // Prevent default behavior for mouse wheel events
    event.preventDefault();
  };

  const handleAlphaKeyDown = (e: React.KeyboardEvent<any>) => {
    if (!onlyAlphabets) return;

    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
      'Enter',
    ];

    // Allow shortcuts like Cmd/Ctrl + A/C/V/X/Z/Y
    if (e.ctrlKey || e.metaKey) return;

    if (allowedControlKeys.includes(e.key)) return;

    // Allow single alphabetic characters and space
    const isAlphaOrSpace = /^[A-Za-z\s]$/.test(e.key);
    if (!isAlphaOrSpace) {
      e.preventDefault();
    }
  };

  const handleAlphaPaste = (e: React.ClipboardEvent<any>) => {
    if (!onlyAlphabets) return;
    const text = e.clipboardData.getData('text');
    const alphaRegex = /^[A-Za-z\s]*$/;
    if (!alphaRegex.test(text)) {
      e.preventDefault();
    }
  };

  // console.log(first)
  return (
    <Box className={`${highLighted ? 'highLighted' : ''}`}>
      <FormLabel className="formLabel">
        {label} {requiredField ? <sup>*</sup> : null}
        {tooltip ? (
          <>
            <CustomPopover
              id=""
              children={popoverContent}
              arrow
              anchorOrigin_vertical="top"
              anchorOrigin_horizontal="center"
              transformOrigin_vertical={145}
              transformOrigin_horizonral={202}
            />
          </>
        ) : // <ToolTipComponent title='Your password must meet the following criteria' theme='light' arrow>
        //   <IconButton disableRipple><HelpOutlineOutlinedIcon/></IconButton>
        //    </ToolTipComponent>

        null}
      </FormLabel>
      {control ? (
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange:onChangeValue, onBlur, value:valueValue, ref } }) => (
            <TextField
              id={id}
              size="small"
              name={name}
              type={type}
              helperText={helperText}
              variant={variant}
              error={error}
              placeholder={placeholder}
              spellCheck
              disabled={disabled}
              // onBlur={onBlur}
              color="primary"
              onChange={e => {
                if (
                  e.target.value.startsWith(' ') &&
                  e.target.value.length === 1
                ) {
                  return;
                }

                if (maxValue && e.target.value.length > maxValue) {
                  return;
                }

                // if (zipCode && !charAndNumbers.test(e.target.value)) {
                //   return // Set value if it matches the regex
                // }
                // if (zipCode) {
                //   if (e.target.value.includes('-')) {
                //     return e.target.value.split('-')[0]
                //   }
                // }

                // Regular expression to prevent multiple spaces between words
                if (/\s{2,}/.test(e.target.value)) {
                  return; // Prevent typing more than one space between words
                }
                if (phone && e.target.value.length >= maxLength) {
                  e.preventDefault();
                  return;
                }

                if (onlyAlphabets) {
                  const alphaRegex = /^[A-Za-z\s]*$/;
                  if (!alphaRegex.test(e.target.value)) {
                    return;
                  }
                }
                onChangeValue(e);
                onChange && onChange(e);
                getOnChangeValues && getOnChangeValues(e);
              }}
              onWheel={handleWheel} // Add the event listener to the input
              value={value  || valueValue || ''}
              // value={value || ''}
              // onChange={onChange}
              inputProps={{
                ...(maxLength && { maxLength: maxLength }),
                ...(readOnly && { readOnly: readOnly }),
              }}
              fullWidth
              InputProps={{
                startAdornment,
                autoComplete: 'off',
                endAdornment: endAdornment,
                onWheel: (event: any) => event.target.blur(),
              }}
              // ref={ref}
              onBlur={onBlurMethod ? onBlurMethod : onBlur}
              onKeyDown={(e: any) => {
                handleAlphaKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
                onKeyDown &&
                  onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
              }}
              onKeyUp={onKeyUp}
              onPaste={(e: any) => {
                handleAlphaPaste(e as React.ClipboardEvent<HTMLInputElement>);
                onPaste && onPaste(e as React.ClipboardEvent<HTMLInputElement>);
              }}
            />
          )}
        />
      ) : (
        <TextField
          id={id}
          size="small"
          name={name}
          type={type}
          helperText={helperText}
          variant={variant}
          error={error}
          placeholder={placeholder}
          disabled={disabled}
          color="primary"
          // value={value || ''}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            if (onlyAlphabets) {
              const alphaRegex = /^[A-Za-z\s]*$/;
              if (!alphaRegex.test(e.target.value)) {
                return;
              }
            }
            onChange && onChange(e as React.ChangeEvent<HTMLInputElement>);
          }}
          defaultValue={defaultValue}
          value={value}
          fullWidth
          InputProps={
            inputProps
              ? inputProps
              : {
                  startAdornment,
                  autoComplete: 'off',
                  endAdornment: endAdornment,
                }
          }
          onKeyDown={(e: any) => {
            handleAlphaKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
            onKeyDown && onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
          }}
          onPaste={(e: any) => {
            handleAlphaPaste(e as React.ClipboardEvent<HTMLInputElement>);
            onPaste && onPaste(e as React.ClipboardEvent<HTMLInputElement>);
          }}
        />
      )}
    </Box>
  );
};

InputField.defaultProps = {
  placeholder: '',
  error: false,
  disabled: false,
  variant: 'outlined',
  type: 'text',
  startAdornment: null,
  endAdornment: null,
};

export default InputField;
